import cn from 'classnames'
import { forwardRef } from 'react'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import type { TEspSponsorshipStatus } from 'partner360/features/executiveSponsorProgram'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../configs/translations'
import { getSponsorInitials } from '../lib/getSponsorInitials'
import { HandshakeIcon, HourglassIcon } from '../lib/icons'

import styles from './CockpitEspChip.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.esp

interface ICockpitEspChipProps {
  status: TEspSponsorshipStatus
  sponsorName: string
  isOpen: boolean
  onToggle: () => void
}

/**
 * The sponsorship on the cockpit's top row, at every stage: the program while the partner has no
 * sponsor, the nomination waiting with a turning hourglass, then the sponsor as a business card,
 * the initials in a ring of the status's colour standing out of the chip. It opens the ESP panel.
 */
export const CockpitEspChip = forwardRef<HTMLButtonElement, ICockpitEspChipProps>(
  ({ status, sponsorName, isOpen, onToggle }, ref) => {
    const t = useSafetyTranslation()
    const hasAvatar = status !== 'notOnboarded'

    return (
      <button
        ref={ref}
        type="button"
        className={cn(styles.root, { [styles.withAvatar]: hasAvatar })}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label={status === 'active' ? `${t(KEYS.sponsor)}: ${sponsorName}` : undefined}
        title={status === 'active' ? sponsorName : undefined}
        onClick={onToggle}
      >
        {hasAvatar && <span className={styles.halo} aria-hidden="true" />}

        {status === 'notOnboarded' ? (
          <>
            <HandshakeIcon className={styles.handshake} />
            <span className={styles.label}>
              <Translation text={KEYS.program} span />
            </span>
          </>
        ) : status === 'pending' ? (
          <>
            <span className={cn(styles.avatar, styles.avatarPending)} aria-hidden="true">
              <HourglassIcon className={styles.hourglass} />
            </span>
            <span className={styles.label}>
              <Translation text={KEYS.pending} span />
            </span>
          </>
        ) : (
          <>
            <span className={cn(styles.avatar, styles.avatarActive)} aria-hidden="true">
              {getSponsorInitials(sponsorName)}
            </span>
            <span className={styles.twoLines}>
              <span className={styles.caption}>
                <Translation text={KEYS.sponsor} span />
              </span>
              <span className={styles.name}>{sponsorName}</span>
            </span>
          </>
        )}

        <i className={cn('kbase kbase-chevron-bottom', styles.chevron)} aria-hidden="true" />
      </button>
    )
  }
)

CockpitEspChip.displayName = 'CockpitEspChip'
